import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, Color, BackgroundColor, FontSize } from '@tiptap/extension-text-style';
import { TableKit } from '@tiptap/extension-table';
import { Placeholder } from '@tiptap/extensions';
import { Icon } from '../../campaign-os';
import { ParamChip } from './ParamChip';
import { CtaButton } from './CtaButton';
import {
  MERGE_PARAMS,
  TEXT_COLORS,
  HIGHLIGHT_COLORS,
  FONT_SIZES,
  IMAGE_SIZES,
  normalizeUrl,
  withParamChips,
  withParamTags,
  exportHtml,
} from '../data/templateModes';
import './scratchPad.css';

/**
 * The scratch pad (user decision, 25 Oct 2026): write a template the way one writes an email,
 * in a workspace that behaves like a document editor.
 *
 *   useScratchEditor — the TipTap editor: headings, colour, highlight, size, lists with indent,
 *                      alignment, links, images (pasted, dropped or chosen; uploaded and placed
 *                      by URL, never base64), tables, a call-to-action button block, vendor
 *                      fields as chips.
 *   ScratchRibbon    — the toolbar row above the canvas.
 *   ScratchSheet     — the paper: fixed Amber header, the editable mail, the fixed footer.
 *   SelectionBubble  — the floating menu over a selection: text, image, button or table tools.
 *
 * The HTML the editor produces goes to the server, which keeps the words and drops everything
 * an inbox cannot render (scratchEmail.ts), then lays the house style over it at send time.
 */

/** The widest an image can be shown in the 600px card. Mirrors MAX_IMAGE_WIDTH on the server. */
const MAX_IMAGE_WIDTH = 520;

const BLOCKS = [
  ['p', 'Normal text'],
  ['h1', 'Heading 1'],
  ['h2', 'Heading 2'],
  ['h3', 'Heading 3'],
];

export const FOOTER_NOTE =
  'You are receiving this because your company is a registered vendor of Amber Enterprises India Limited. This notice was sent by Genessence Consulting Private Limited on behalf of Amber.';

/** The image's own width, so Outlook (which reads only the width attribute) shows it at true size. */
function naturalWidthOf(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(Math.min(img.naturalWidth || MAX_IMAGE_WIDTH, MAX_IMAGE_WIDTH));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(MAX_IMAGE_WIDTH);
    };
    img.src = url;
  });
}

const imageFilesOf = (list) => Array.from(list ?? []).filter((f) => f.type && f.type.startsWith('image/'));

/**
 * The CSS zoom an element is drawn under (the app draws itself through #app-scale-wrapper's
 * zoom). getBoundingClientRect answers in screen pixels; a `top`/`left` style is read in the
 * element's own, zoomed pixels — so screen distances are divided by this before they are set.
 */
function zoomOf(el) {
  let z = 1;
  for (let n = el; n; n = n.parentElement) {
    const v = parseFloat(getComputedStyle(n).zoom);
    if (v && v !== 1) z *= v;
  }
  return z;
}

// ---------------------------------------------------------------------------------- editor

export function useScratchEditor({ value, onChange, onUploadImage, onError }) {
  const [uploading, setUploading] = useState(0);
  const insertRef = useRef(null);
  const lastEmitted = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, autolink: true, defaultProtocol: 'https', HTMLAttributes: { rel: 'noopener' } },
        codeBlock: false,
        code: false,
      }),
      Image.configure({ inline: true, allowBase64: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      BackgroundColor,
      FontSize,
      TableKit.configure({ table: { resizable: false } }),
      Placeholder.configure({ placeholder: 'Write the mail here — or paste text, a screenshot, a table from Excel.' }),
      ParamChip,
      CtaButton,
    ],
    content: withParamChips(value ?? ''),
    editorProps: {
      attributes: { class: 'sp-editor', spellcheck: 'true' },
      // A pasted or dropped screenshot is uploaded and placed by URL, never kept as base64.
      handlePaste: (_view, event) => {
        const files = imageFilesOf(event.clipboardData?.files);
        if (!files.length) return false;
        event.preventDefault();
        files.forEach((f) => insertRef.current?.(f));
        return true;
      },
      handleDrop: (_view, event, _slice, moved) => {
        if (moved) return false;
        const files = imageFilesOf(event.dataTransfer?.files);
        if (!files.length) return false;
        event.preventDefault();
        files.forEach((f) => insertRef.current?.(f));
        return true;
      },
    },
    onUpdate: ({ editor: e }) => {
      const html = exportHtml(e.getHTML());
      lastEmitted.current = html;
      onChange(html);
    },
  });

  // A template loaded after the pad mounted (the full page, a restored draft) lands in the editor.
  useEffect(() => {
    if (!editor) return;
    const next = value ?? '';
    if (next === lastEmitted.current) return;
    if (exportHtml(editor.getHTML()) === exportHtml(withParamChips(next))) return;
    editor.commands.setContent(withParamChips(next), { emitUpdate: false });
  }, [editor, value]);

  const insertImageFile = useCallback(
    async (file) => {
      if (!editor || !file) return;
      setUploading((n) => n + 1);
      try {
        const [stored, width] = await Promise.all([onUploadImage(file), naturalWidthOf(file)]);
        editor
          .chain()
          .focus()
          .setImage({ src: stored.url, alt: file.name.replace(/\.[a-z0-9]+$/i, ''), width })
          .run();
      } catch (err) {
        onError?.(err);
      } finally {
        setUploading((n) => n - 1);
      }
    },
    [editor, onUploadImage, onError]
  );
  insertRef.current = insertImageFile;

  return { editor, insertImageFile, uploading };
}

// ---------------------------------------------------------------------------------- bits

function ToolButton({ icon, label, active = false, disabled = false, onClick, className = '' }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`sp-tb ${active ? 'is-active' : ''} ${className}`}
    >
      <Icon name={icon} className="text-[19px]" />
    </button>
  );
}

/** Closes on an outside click or Escape. Anchored under its parent (position: relative). */
function Pop({ open, onClose, children, className = '', align = 'left' }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !ref.current.parentElement?.contains(e.target)) onClose();
    };
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  if (!open) return null;
  // A button in the popover must not take focus from the editor (the selection would collapse
  // and the command would land nowhere); a text field in it must.
  const keepFocus = (e) => {
    e.stopPropagation();
    if (!e.target.closest('input, textarea, select')) e.preventDefault();
  };
  return (
    <div ref={ref} className={`sp-popover sp-popover-${align} ${className}`} onMouseDown={keepFocus}>
      {children}
    </div>
  );
}

const POP_FIELD = 'sp-field';
const POP_BTN = 'sp-btn-primary';

function Swatches({ colors, current, onPick, onClear, clearLabel }) {
  return (
    <div className="sp-swatches">
      {colors.map((c) => (
        <button
          key={c.key}
          type="button"
          title={c.label}
          aria-label={c.label}
          aria-pressed={current === c.value}
          className={`sp-swatch ${current === c.value ? 'is-active' : ''}`}
          style={{ background: c.value }}
          onClick={() => onPick(c.value)}
        />
      ))}
      <button type="button" className="sp-swatch sp-swatch-clear" title={clearLabel} aria-label={clearLabel} onClick={onClear}>
        <Icon name="format_color_reset" className="text-[15px]" />
      </button>
    </div>
  );
}

/** The link / image / button forms, shared by the ribbon and the selection bubble. */
function LinkForm({ editor, onDone }) {
  const [url, setUrl] = useState(editor.getAttributes('link').href ?? '');
  const apply = () => {
    const href = normalizeUrl(url);
    const chain = editor.chain().focus().extendMarkRange('link');
    if (href) chain.setLink({ href }).run();
    else chain.unsetLink().run();
    onDone();
  };
  return (
    <div>
      <p className="sp-pop-title">Link the selected text to</p>
      <input
        autoFocus
        className={POP_FIELD}
        value={url}
        placeholder="https://…"
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') apply();
        }}
      />
      <div className="sp-pop-actions">
        {editor.isActive('link') ? (
          <button
            type="button"
            className="sp-btn-danger"
            onClick={() => {
              editor.chain().focus().extendMarkRange('link').unsetLink().run();
              onDone();
            }}
          >
            Remove link
          </button>
        ) : null}
        <button type="button" className={POP_BTN} onClick={apply}>
          Apply
        </button>
      </div>
    </div>
  );
}

function ImageForm({ editor, onFile, onDone }) {
  const [url, setUrl] = useState('');
  const fileRef = useRef(null);
  const applyUrl = () => {
    const src = normalizeUrl(url);
    if (src) editor.chain().focus().setImage({ src, alt: 'Image', width: MAX_IMAGE_WIDTH }).run();
    onDone();
  };
  return (
    <div>
      <button type="button" className="sp-drop" onClick={() => fileRef.current?.click()}>
        <Icon name="upload" className="text-[20px]" />
        <span>
          <strong>Choose an image</strong>
          <br />
          PNG, JPEG, GIF or WebP, up to 2 MB
        </span>
      </button>
      <p className="sp-pop-hint">You can also paste a screenshot or drop a file straight onto the mail.</p>
      <div className="sp-pop-row">
        <input
          className={POP_FIELD}
          value={url}
          placeholder="…or paste an image link (https://)"
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applyUrl();
          }}
        />
        <button type="button" className={POP_BTN} onClick={applyUrl}>
          Add
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          onDone();
          if (file) onFile(file);
        }}
      />
    </div>
  );
}

function ButtonForm({ editor, onDone, existing = null }) {
  const [label, setLabel] = useState(existing?.label ?? 'Open the portal');
  const [href, setHref] = useState(existing?.href ?? '');
  const [align, setAlign] = useState(existing?.align ?? 'left');
  const apply = () => {
    const url = normalizeUrl(href);
    if (!label.trim() || !url) return;
    if (existing) {
      // Updating the node replaces it; select it again so the bubble stays on it.
      const pos = editor.state.selection.from;
      editor.chain().focus().updateCta({ label: label.trim(), href: url, align }).setNodeSelection(pos).run();
    } else {
      editor.chain().focus().insertCta({ label: label.trim(), href: url, align }).run();
    }
    onDone();
  };
  return (
    <div>
      <p className="sp-pop-title">{existing ? 'Edit the button' : 'Add a button'}</p>
      <label className="sp-pop-label">Button text</label>
      <input autoFocus className={POP_FIELD} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Open the portal" />
      <label className="sp-pop-label">Opens</label>
      <input
        className={POP_FIELD}
        value={href}
        onChange={(e) => setHref(e.target.value)}
        placeholder="https://…"
        onKeyDown={(e) => {
          if (e.key === 'Enter') apply();
        }}
      />
      <label className="sp-pop-label">Position</label>
      <div className="sp-seg">
        {[
          ['left', 'format_align_left', 'Left'],
          ['center', 'format_align_center', 'Centre'],
          ['right', 'format_align_right', 'Right'],
        ].map(([k, icon, t]) => (
          <button key={k} type="button" title={t} aria-pressed={align === k} className={`sp-seg-btn ${align === k ? 'is-active' : ''}`} onClick={() => setAlign(k)}>
            <Icon name={icon} className="text-[17px]" />
          </button>
        ))}
      </div>
      <div className="sp-pop-actions">
        <button type="button" className={POP_BTN} onClick={apply} disabled={!label.trim() || !href.trim()}>
          {existing ? 'Update' : 'Insert button'}
        </button>
      </div>
    </div>
  );
}

function TableTools({ editor, onDone }) {
  const run = (fn) => {
    fn(editor.chain().focus()).run();
    onDone?.();
  };
  const inTable = editor.isActive('table');
  if (!inTable) {
    return (
      <div>
        <p className="sp-pop-title">Insert a table</p>
        <div className="sp-grid-pick">
          {[2, 3, 4].map((rows) =>
            [2, 3, 4].map((cols) => (
              <button
                key={`${rows}x${cols}`}
                type="button"
                className="sp-grid-cell"
                onClick={() => run((c) => c.insertTable({ rows, cols, withHeaderRow: true }))}
              >
                {rows}×{cols}
              </button>
            ))
          )}
        </div>
        <p className="sp-pop-hint">The first row is a header row. A table pasted from Excel keeps its cells.</p>
      </div>
    );
  }
  const items = [
    ['Row above', 'add_row_above', (c) => c.addRowBefore()],
    ['Row below', 'add_row_below', (c) => c.addRowAfter()],
    ['Delete row', 'delete', (c) => c.deleteRow()],
    ['Column left', 'add_column_left', (c) => c.addColumnBefore()],
    ['Column right', 'add_column_right', (c) => c.addColumnAfter()],
    ['Delete column', 'delete', (c) => c.deleteColumn()],
    ['Header row on/off', 'table_rows', (c) => c.toggleHeaderRow()],
    ['Delete table', 'delete_forever', (c) => c.deleteTable()],
  ];
  return (
    <div>
      <p className="sp-pop-title">Table</p>
      <div className="sp-menu">
        {items.map(([label, icon, fn]) => (
          <button key={label} type="button" className="sp-menu-item" onClick={() => run(fn)}>
            <Icon name={icon} className="text-[17px]" />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------- ribbon

/** The toolbar row. `rightSlot` is where the document puts Details and the shortcuts help. */
export function ScratchRibbon({ editor, insertImageFile, uploading, rightSlot = null }) {
  const [pop, setPop] = useState(null);
  const closePop = useCallback(() => setPop(null), []);
  const toggle = (which) => setPop((p) => (p === which ? null : which));

  const s = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e
        ? {
            bold: e.isActive('bold'),
            italic: e.isActive('italic'),
            underline: e.isActive('underline'),
            strike: e.isActive('strike'),
            bullet: e.isActive('bulletList'),
            ordered: e.isActive('orderedList'),
            quote: e.isActive('blockquote'),
            link: e.isActive('link'),
            table: e.isActive('table'),
            left: e.isActive({ textAlign: 'left' }),
            center: e.isActive({ textAlign: 'center' }),
            right: e.isActive({ textAlign: 'right' }),
            justify: e.isActive({ textAlign: 'justify' }),
            block: e.isActive('heading', { level: 1 }) ? 'h1' : e.isActive('heading', { level: 2 }) ? 'h2' : e.isActive('heading', { level: 3 }) ? 'h3' : 'p',
            size: e.getAttributes('textStyle').fontSize ?? '',
            color: e.getAttributes('textStyle').color ?? '',
            highlight: e.getAttributes('textStyle').backgroundColor ?? '',
            canUndo: e.can().undo(),
            canRedo: e.can().redo(),
            canIndent: e.can().sinkListItem('listItem'),
            canOutdent: e.can().liftListItem('listItem'),
          }
        : null,
  });

  if (!editor || !s) return null;
  const chain = () => editor.chain().focus();

  const setBlock = (v) => {
    if (v === 'p') chain().setParagraph().run();
    else chain().setHeading({ level: Number(v.slice(1)) }).run();
  };
  const setSize = (px) => {
    if (px) chain().setFontSize(`${px}px`).run();
    else chain().unsetFontSize().run();
  };

  return (
    <div className="sp-toolbar">
      <ToolButton icon="undo" label="Undo (Ctrl+Z)" disabled={!s.canUndo} onClick={() => chain().undo().run()} />
      <ToolButton icon="redo" label="Redo (Ctrl+Y)" disabled={!s.canRedo} onClick={() => chain().redo().run()} />
      <span className="sp-toolbar-sep" />
      <select aria-label="Text style" className="sp-select sp-select-wide" value={s.block} onMouseDown={(e) => e.stopPropagation()} onChange={(e) => setBlock(e.target.value)}>
        {BLOCKS.map(([k, label]) => (
          <option key={k} value={k}>
            {label}
          </option>
        ))}
      </select>
      <select
        aria-label="Text size"
        className="sp-select"
        value={FONT_SIZES.find((f) => (f.px ? `${f.px}px` : '') === s.size)?.key ?? 'normal'}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => setSize(FONT_SIZES.find((f) => f.key === e.target.value)?.px ?? null)}
      >
        {FONT_SIZES.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </select>
      <span className="sp-toolbar-sep" />
      <ToolButton icon="format_bold" label="Bold (Ctrl+B)" active={s.bold} onClick={() => chain().toggleBold().run()} />
      <ToolButton icon="format_italic" label="Italic (Ctrl+I)" active={s.italic} onClick={() => chain().toggleItalic().run()} />
      <ToolButton icon="format_underlined" label="Underline (Ctrl+U)" active={s.underline} onClick={() => chain().toggleUnderline().run()} />
      <ToolButton icon="strikethrough_s" label="Strikethrough" active={s.strike} onClick={() => chain().toggleStrike().run()} />
      <div className="relative">
        <ToolButton icon="format_color_text" label="Text colour and highlight" active={pop === 'color' || Boolean(s.color) || Boolean(s.highlight)} onClick={() => toggle('color')} />
        <span className="sp-color-bar" style={{ background: s.color || '#15263D' }} />
        <Pop open={pop === 'color'} onClose={closePop}>
          <p className="sp-pop-title">Text colour</p>
          <Swatches colors={TEXT_COLORS} current={s.color} onPick={(v) => chain().setColor(v).run()} onClear={() => chain().unsetColor().run()} clearLabel="Default colour" />
          <p className="sp-pop-title mt-3">Highlight</p>
          <Swatches colors={HIGHLIGHT_COLORS} current={s.highlight} onPick={(v) => chain().setBackgroundColor(v).run()} onClear={() => chain().unsetBackgroundColor().run()} clearLabel="No highlight" />
        </Pop>
      </div>
      <span className="sp-toolbar-sep" />
      <ToolButton icon="format_align_left" label="Align left" active={s.left} onClick={() => chain().setTextAlign('left').run()} />
      <ToolButton icon="format_align_center" label="Align centre" active={s.center} onClick={() => chain().setTextAlign('center').run()} />
      <ToolButton icon="format_align_right" label="Align right" active={s.right} onClick={() => chain().setTextAlign('right').run()} />
      <ToolButton icon="format_align_justify" label="Justify" active={s.justify} onClick={() => chain().setTextAlign('justify').run()} />
      <span className="sp-toolbar-sep" />
      <ToolButton icon="format_list_bulleted" label="Bulleted list" active={s.bullet} onClick={() => chain().toggleBulletList().run()} />
      <ToolButton icon="format_list_numbered" label="Numbered list" active={s.ordered} onClick={() => chain().toggleOrderedList().run()} />
      <ToolButton icon="format_indent_decrease" label="Outdent (Shift+Tab)" disabled={!s.canOutdent} onClick={() => chain().liftListItem('listItem').run()} />
      <ToolButton icon="format_indent_increase" label="Indent (Tab)" disabled={!s.canIndent} onClick={() => chain().sinkListItem('listItem').run()} />
      <span className="sp-toolbar-sep" />
      <ToolButton icon="format_quote" label="Highlight box" active={s.quote} onClick={() => chain().toggleBlockquote().run()} />
      <ToolButton icon="horizontal_rule" label="Divider line" onClick={() => chain().setHorizontalRule().run()} />
      <div className="relative">
        <ToolButton icon="table" label="Table" active={pop === 'table' || s.table} onClick={() => toggle('table')} />
        <Pop open={pop === 'table'} onClose={closePop}>
          <TableTools editor={editor} onDone={closePop} />
        </Pop>
      </div>
      <span className="sp-toolbar-sep" />
      <div className="relative">
        <ToolButton icon="link" label="Link (Ctrl+K)" active={s.link || pop === 'link'} onClick={() => toggle('link')} />
        <Pop open={pop === 'link'} onClose={closePop}>
          <LinkForm editor={editor} onDone={closePop} />
        </Pop>
      </div>
      <div className="relative">
        <ToolButton icon="add_photo_alternate" label="Image" active={pop === 'image'} onClick={() => toggle('image')} />
        <Pop open={pop === 'image'} onClose={closePop}>
          <ImageForm editor={editor} onFile={insertImageFile} onDone={closePop} />
        </Pop>
      </div>
      <div className="relative">
        <ToolButton icon="smart_button" label="Button" active={pop === 'button'} onClick={() => toggle('button')} />
        <Pop open={pop === 'button'} onClose={closePop}>
          <ButtonForm editor={editor} onDone={closePop} />
        </Pop>
      </div>
      <span className="sp-toolbar-sep" />
      <div className="relative">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => toggle('param')} className={`sp-tb-label ${pop === 'param' ? 'is-active' : ''}`} title="Insert a vendor field, filled in per vendor at send time">
          <Icon name="data_object" className="text-[18px]" />
          Vendor field
          <Icon name="expand_more" className="text-[16px]" />
        </button>
        <Pop open={pop === 'param'} onClose={closePop} className="!min-w-[250px]">
          <p className="sp-pop-title">Filled in for each vendor</p>
          <div className="sp-menu">
            {MERGE_PARAMS.map((p) => (
              <button
                key={p.key}
                type="button"
                className="sp-menu-item sp-menu-item-split"
                onClick={() => {
                  chain().insertParam(p.key).run();
                  closePop();
                }}
              >
                <span className="font-semibold">{p.label}</span>
                <span className="mono sp-menu-hint">e.g. {p.sample}</span>
              </button>
            ))}
          </div>
        </Pop>
      </div>
      <span className="sp-toolbar-sep" />
      <ToolButton icon="format_clear" label="Clear formatting" onClick={() => chain().unsetAllMarks().clearNodes().run()} />
      <span className="sp-toolbar-grow" />
      {uploading > 0 ? (
        <span className="sp-uploading">
          <Icon name="progress_activity" className="animate-spin text-[16px]" />
          Uploading image…
        </span>
      ) : null}
      {rightSlot}
    </div>
  );
}

// ---------------------------------------------------------------------------------- sheet

/** The paper: an optional envelope (name/subject), the fixed header, the mail, the fixed footer. */
export function ScratchSheet({ editor, envelope = null, zoom = 1 }) {
  return (
    <div className="sp-canvas-inner" style={{ zoom }}>
      <div className="sp-paper">
        {envelope}
        <div className="relative">
          <span className="sp-fixed-tag">Fixed header</span>
          <div className="sp-head">
            <img src="/email/amber-logo.png" alt="Amber Enterprises India Limited" width="150" height="52" />
            <div className="sp-head-caption">Amber Compliance</div>
          </div>
          <div className="sp-rule-navy" />
          <div className="sp-rule-amber" />
        </div>
        <EditorContent editor={editor} />
        <div className="relative">
          <span className="sp-fixed-tag">Fixed footer</span>
          <div className="sp-foot">
            <p>{FOOTER_NOTE}</p>
            <p>© {new Date().getFullYear()} Amber Enterprises India Limited. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------- bubble

/**
 * The floating menu over the current selection, inside `containerRef` (the scrolling canvas,
 * position: relative). Text: the quick marks, link and colour. An image: size and placement.
 * A button: edit, placement, remove. Inside a table: row and column tools.
 */
export function SelectionBubble({ editor, containerRef }) {
  const [box, setBox] = useState(null); // { left, top, kind }
  const [pop, setPop] = useState(null);
  const bubbleRef = useRef(null);
  // Read through a ref: a blur transaction fires the instant a form inside the bubble takes
  // focus, before React has re-run the effects with the new popover state.
  const popRef = useRef(null);
  popRef.current = pop;

  const kindOf = useCallback(() => {
    if (!editor) return null;
    const inBubble = Boolean(bubbleRef.current && bubbleRef.current.contains(document.activeElement));
    if (!editor.isFocused && !popRef.current && !inBubble) return null;
    const sel = editor.state.selection;
    const node = sel.node;
    if (node?.type.name === 'image') return 'image';
    if (node?.type.name === 'ctaButton') return 'button';
    if (node) return null;
    if (editor.isActive('table') && sel.empty) return 'table';
    if (sel.empty) return null;
    return 'text';
  }, [editor]);

  const place = useCallback(() => {
    const container = containerRef.current;
    if (!editor || !container) return setBox(null);
    const kind = kindOf();
    if (!kind) return setBox(null);
    const sel = editor.state.selection;
    const crect = container.getBoundingClientRect();
    let rect;
    if (sel.node) {
      const dom = editor.view.nodeDOM(sel.from);
      rect = dom?.getBoundingClientRect?.();
    } else if (kind === 'table') {
      const dom = editor.view.domAtPos(sel.from).node;
      const cell = (dom.nodeType === 1 ? dom : dom.parentElement)?.closest('td, th');
      rect = cell?.getBoundingClientRect();
    } else {
      const a = editor.view.coordsAtPos(sel.from);
      const b = editor.view.coordsAtPos(sel.to);
      rect = { left: Math.min(a.left, b.left), right: Math.max(a.right, b.right), top: Math.min(a.top, b.top), bottom: Math.max(a.bottom, b.bottom) };
    }
    if (!rect) return setBox(null);
    const z = zoomOf(container);
    const width = bubbleRef.current?.offsetWidth ?? 320;
    const cw = crect.width / z;
    const left = Math.max(8 + width / 2, Math.min(cw - 8 - width / 2, ((rect.left + rect.right) / 2 - crect.left) / z));
    const top = (rect.top - crect.top) / z + container.scrollTop - 12;
    setBox({ left, top, kind });
  }, [editor, containerRef, kindOf]);

  useEffect(() => {
    if (!editor) return undefined;
    const onSel = () => place();
    // A blur is judged a moment later: focus may have moved into the bubble's own form.
    const onBlur = () => setTimeout(place, 120);
    editor.on('selectionUpdate', onSel);
    editor.on('transaction', onSel);
    editor.on('focus', onSel);
    editor.on('blur', onBlur);
    const container = containerRef.current;
    container?.addEventListener('scroll', onSel, { passive: true });
    window.addEventListener('resize', onSel);
    document.addEventListener('focusin', onBlur);
    return () => {
      editor.off('selectionUpdate', onSel);
      editor.off('transaction', onSel);
      editor.off('focus', onSel);
      editor.off('blur', onBlur);
      container?.removeEventListener('scroll', onSel);
      window.removeEventListener('resize', onSel);
      document.removeEventListener('focusin', onBlur);
    };
  }, [editor, containerRef, place]);

  useLayoutEffect(() => {
    if (box) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box?.kind]);

  const s = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e
        ? {
            bold: e.isActive('bold'),
            italic: e.isActive('italic'),
            underline: e.isActive('underline'),
            link: e.isActive('link'),
            color: e.getAttributes('textStyle').color ?? '',
            highlight: e.getAttributes('textStyle').backgroundColor ?? '',
            imageWidth: Number(e.getAttributes('image').width) || MAX_IMAGE_WIDTH,
            left: e.isActive({ textAlign: 'left' }),
            center: e.isActive({ textAlign: 'center' }),
            right: e.isActive({ textAlign: 'right' }),
            cta: e.getAttributes('ctaButton'),
          }
        : null,
  });

  if (!editor || !box || !s) return null;
  const chain = () => editor.chain().focus();
  const closePop = () => setPop(null);
  const stop = (e) => e.preventDefault();
  /** Change a selected node's attributes and keep it selected (the change replaces the node). */
  const onNode = (fn) => {
    const pos = editor.state.selection.from;
    fn(chain()).setNodeSelection(pos).run();
  };
  /** Align the paragraph an inline node sits in: a node selection covers only the node itself. */
  const alignAround = (align) => {
    const pos = editor.state.selection.from;
    chain().setTextSelection(pos + 1).setTextAlign(align).setNodeSelection(pos).run();
  };

  return (
    <div ref={bubbleRef} className="sp-bubble" style={{ left: box.left, top: box.top }} onMouseDown={stop}>
      {box.kind === 'text' ? (
        <>
          <ToolButton icon="format_bold" label="Bold" active={s.bold} onClick={() => chain().toggleBold().run()} />
          <ToolButton icon="format_italic" label="Italic" active={s.italic} onClick={() => chain().toggleItalic().run()} />
          <ToolButton icon="format_underlined" label="Underline" active={s.underline} onClick={() => chain().toggleUnderline().run()} />
          <span className="sp-toolbar-sep" />
          <div className="relative">
            <ToolButton icon="link" label="Link" active={s.link || pop === 'link'} onClick={() => setPop((p) => (p === 'link' ? null : 'link'))} />
            <Pop open={pop === 'link'} onClose={closePop} align="center">
              <LinkForm editor={editor} onDone={closePop} />
            </Pop>
          </div>
          <div className="relative">
            <ToolButton icon="format_color_text" label="Colour" active={pop === 'color' || Boolean(s.color)} onClick={() => setPop((p) => (p === 'color' ? null : 'color'))} />
            <Pop open={pop === 'color'} onClose={closePop} align="center">
              <p className="sp-pop-title">Text colour</p>
              <Swatches colors={TEXT_COLORS} current={s.color} onPick={(v) => chain().setColor(v).run()} onClear={() => chain().unsetColor().run()} clearLabel="Default colour" />
              <p className="sp-pop-title mt-3">Highlight</p>
              <Swatches colors={HIGHLIGHT_COLORS} current={s.highlight} onPick={(v) => chain().setBackgroundColor(v).run()} onClear={() => chain().unsetBackgroundColor().run()} clearLabel="No highlight" />
            </Pop>
          </div>
          <div className="relative">
            <ToolButton icon="data_object" label="Vendor field" active={pop === 'param'} onClick={() => setPop((p) => (p === 'param' ? null : 'param'))} />
            <Pop open={pop === 'param'} onClose={closePop} align="center" className="!min-w-[220px]">
              <div className="sp-menu">
                {MERGE_PARAMS.map((p) => (
                  <button key={p.key} type="button" className="sp-menu-item" onClick={() => { chain().insertParam(p.key).run(); closePop(); }}>
                    {p.label}
                  </button>
                ))}
              </div>
            </Pop>
          </div>
        </>
      ) : null}

      {box.kind === 'image' ? (
        <>
          <span className="sp-bubble-label">Image</span>
          {IMAGE_SIZES.map((sz) => (
            <button
              key={sz.key}
              type="button"
              className={`sp-chip-btn ${s.imageWidth === sz.width ? 'is-active' : ''}`}
              title={`${sz.label} (${sz.width}px)`}
              onClick={() => onNode((c) => c.updateAttributes('image', { width: sz.width }))}
            >
              {sz.label}
            </button>
          ))}
          <span className="sp-toolbar-sep" />
          <ToolButton icon="format_align_left" label="Place left" active={s.left} onClick={() => alignAround('left')} />
          <ToolButton icon="format_align_center" label="Place in the centre" active={s.center} onClick={() => alignAround('center')} />
          <ToolButton icon="format_align_right" label="Place right" active={s.right} onClick={() => alignAround('right')} />
          <span className="sp-toolbar-sep" />
          <ToolButton icon="delete" label="Remove image" className="is-danger" onClick={() => chain().deleteSelection().run()} />
        </>
      ) : null}

      {box.kind === 'button' ? (
        <>
          <span className="sp-bubble-label">Button</span>
          <div className="relative">
            <button type="button" className="sp-chip-btn" onClick={() => setPop((p) => (p === 'cta' ? null : 'cta'))}>
              <Icon name="edit" className="text-[15px]" /> Edit text &amp; link
            </button>
            <Pop open={pop === 'cta'} onClose={closePop} align="center">
              <ButtonForm editor={editor} existing={s.cta} onDone={closePop} />
            </Pop>
          </div>
          <span className="sp-toolbar-sep" />
          {[
            ['left', 'format_align_left', 'Left'],
            ['center', 'format_align_center', 'Centre'],
            ['right', 'format_align_right', 'Right'],
          ].map(([k, icon, t]) => (
            <ToolButton key={k} icon={icon} label={`Place ${t.toLowerCase()}`} active={s.cta?.align === k} onClick={() => onNode((c) => c.updateCta({ align: k }))} />
          ))}
          <span className="sp-toolbar-sep" />
          <ToolButton icon="delete" label="Remove button" className="is-danger" onClick={() => chain().deleteSelection().run()} />
        </>
      ) : null}

      {box.kind === 'table' ? (
        <>
          <span className="sp-bubble-label">Table</span>
          <ToolButton icon="add_row_below" label="Row below" onClick={() => chain().addRowAfter().run()} />
          <ToolButton icon="add_column_right" label="Column right" onClick={() => chain().addColumnAfter().run()} />
          <ToolButton icon="table_rows" label="Header row on/off" onClick={() => chain().toggleHeaderRow().run()} />
          <span className="sp-toolbar-sep" />
          <ToolButton icon="delete_sweep" label="Delete row" onClick={() => chain().deleteRow().run()} />
          <ToolButton icon="view_column" label="Delete column" onClick={() => chain().deleteColumn().run()} />
          <ToolButton icon="delete_forever" label="Delete table" className="is-danger" onClick={() => chain().deleteTable().run()} />
        </>
      ) : null}
    </div>
  );
}
